import { HealthDto, PingJobDto } from "@growth/dto";
import { Badge, Button, Field, Input } from "@growth/ui";
import { CircleCheck, CircleX, LoaderCircle, Send } from "lucide-react";
import { type FormEvent, useState } from "react";

import { apiErrorMessage } from "../lib/api-error";
import { useHealth, usePingJob, useStartPing } from "../lib/system";

const UpDown = ({ up }: { up: boolean }) =>
  up ? <Badge tone="success">Up</Badge> : <Badge className="text-red-700 ring-red-200">Down</Badge>;

const secondsAgo = (iso: string) => Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 1000));

const HealthTable = ({ health }: { health: HealthDto }) => (
  <dl className="divide-y divide-line rounded-lg border border-line bg-white text-sm">
    <div className="flex items-center justify-between px-4 py-3">
      <dt>Database</dt>
      <dd>
        <UpDown up={health.database === "up"} />
      </dd>
    </div>
    <div className="flex items-center justify-between px-4 py-3">
      <dt>Job queue (Redis)</dt>
      <dd>
        <UpDown up={health.redis === "up"} />
      </dd>
    </div>
    <div className="flex items-center justify-between gap-4 px-4 py-3">
      <dt>
        Worker
        {health.worker.heartbeat && (
          <span className="block text-xs text-muted">
            {health.worker.heartbeat.host} · pid {health.worker.heartbeat.pid} · last seen{" "}
            {secondsAgo(health.worker.heartbeat.at)} s ago
          </span>
        )}
      </dt>
      <dd>
        <UpDown up={health.worker.status === "up"} />
      </dd>
    </div>
  </dl>
);

const JobOutcome = ({ job }: { job: PingJobDto }) => {
  if (job.state === "completed" && job.result) {
    return (
      <p className="flex items-start gap-2 text-sm">
        <CircleCheck className="mt-0.5 size-4 shrink-0 text-emerald-600" />
        <span>
          The worker answered (job {job.id}) from {job.result.worker.host}, pid{" "}
          {job.result.worker.pid}, and reached the database.
          {job.result.note && <> Note: “{job.result.note}”.</>}
        </span>
      </p>
    );
  }
  if (job.state === "failed") {
    return (
      <p className="flex items-start gap-2 text-sm text-red-700">
        <CircleX className="mt-0.5 size-4 shrink-0" />
        Job {job.id} failed: {job.error ?? "no reason given"}
      </p>
    );
  }
  return (
    <p className="flex items-center gap-2 text-sm text-muted">
      <LoaderCircle className="size-4 animate-spin" />
      Job {job.id} is {job.state}. If the worker isn't running, it waits here until it starts.
    </p>
  );
};

/** Is everything behind the app running? A place to check the worker end to end. */
export const SystemPage = () => {
  const health = useHealth();
  const startPing = useStartPing();
  const [note, setNote] = useState("");
  const [jobId, setJobId] = useState<string | null>(null);
  const job = usePingJob(jobId);

  const send = (event: FormEvent) => {
    event.preventDefault();
    startPing.mutate(note, { onSuccess: (started) => setJobId(started.id) });
  };

  return (
    <section className="max-w-2xl space-y-8">
      <header>
        <p className="text-xs font-semibold uppercase tracking-widest text-muted">Admin</p>
        <h1 className="font-serif text-3xl text-ink">System</h1>
      </header>

      <div className="space-y-3">
        <h2 className="text-sm font-semibold">Status</h2>
        {health.data ? (
          <HealthTable health={health.data} />
        ) : health.isError ? (
          <p className="text-sm text-red-700">{apiErrorMessage(health.error)}</p>
        ) : (
          <LoaderCircle className="size-5 animate-spin text-muted" aria-label="Loading status" />
        )}
      </div>

      <form onSubmit={send} className="space-y-3">
        <h2 className="text-sm font-semibold">Test the worker</h2>
        <p className="text-sm text-muted">
          Sends a job through the queue. The worker picks it up, checks the database and sends the
          answer back.
        </p>
        <Field label="Note (optional)" counter={{ value: note.length, max: 200 }}>
          {(props) => (
            <Input
              {...props}
              value={note}
              maxLength={200}
              onChange={(event) => setNote(event.target.value)}
              placeholder="Anything, to see it come back"
            />
          )}
        </Field>
        <Button type="submit" disabled={startPing.isPending}>
          <Send className="size-4" />
          Send test job
        </Button>
        {startPing.isError && (
          <p className="text-sm text-red-700">{apiErrorMessage(startPing.error)}</p>
        )}
        {job.data && <JobOutcome job={job.data} />}
      </form>
    </section>
  );
};
